<template>
  <ConfigSection
    heading="PUBLISHING.DETAIL.PRODUCTS.ADVANCED.PRODUCT.PREVIEW"
    :subheading="productTypeName"
  >
    <ul class="images">
      <li v-for="image in images" :key="image.viewId">
        <Badge
          v-if="image.primary"
          v-tooltip.bottom="{
            content: $t(
              'PUBLISHING.DETAIL.PRODUCTS.ADVANCED.PRODUCT.PRIMARY_HINT',
            ),
            hideOnTargetClick: false,
          }"
          class="primary-badge"
          type="light"
          >{{
            $t('PUBLISHING.DETAIL.PRODUCTS.ADVANCED.PRODUCT.PRIMARY')
          }}</Badge
        >
        <img :src="image.img" alt="" />
      </li>
    </ul>
  </ConfigSection>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import compositionHelper from 'commons/composition/compositionHelper';
import ConfigSection from 'src/app/components/publishing/ConfigSection.vue';
import Badge from 'src/app/components/badge/Badge.vue';
import publishingCoreService, {
  PlacementPreview,
} from 'commons/api/publishingCoreService/publishingCoreService';
import {
  AdvancedComposition,
  HotspotConfigs,
} from '@spreadgroup/common-partner-lib/composition';
import { CompositionSpecificCategoryProductType } from 'commons/composition/compositionCategoryHelper';

interface Props {
  defaultAppearance: number;
  hotspotConfig: HotspotConfigs;
  composition: AdvancedComposition;
  productType: CompositionSpecificCategoryProductType;
}

const props = defineProps<Props>();

const store = useStore();
const previews = ref<PlacementPreview[]>([]);

const productTypeViews = computed(
  () =>
    store.state.publishingCore.translations.productTypes[props.productType.id]
      ?.views,
);

const productTypeName = computed(
  () =>
    store.state.publishingCore.translations.productTypes[props.productType.id]
      ?.name,
);

const images = computed(() => {
  if (previews.value.length === 0) return [];

  const filteredViews = Object.keys(productTypeViews.value || {}).filter(
    (viewId) =>
      previews.value.some(
        (preview) => preview.defaultViewId === Number(viewId),
      ),
  );

  return filteredViews.map((viewId) => {
    const previewsForView = previews.value.filter(
      (preview) => preview.defaultViewId === Number(viewId),
    );
    const currentPreview = previewsForView.find((preview) => {
      return Object.entries(props.hotspotConfig).find(([hotspot, configs]) => {
        return (
          preview.hotspot === hotspot &&
          configs[0].designId === preview.designId
        );
      });
    });

    if (
      currentPreview?.defaultViewId === Number(viewId) &&
      currentPreview?.vpString
    ) {
      return {
        viewId,
        primary: currentPreview?.designId === props.composition.mainDesign.id,
        img: `${compositionHelper.getProductImage(
          currentPreview.vpString,
          currentPreview.defaultViewId,
        )}?appearanceId=${props.defaultAppearance}`,
      };
    }
    return {
      viewId,
      primary: false,
      img: compositionHelper.getProductTypeImage(
        props.productType.id,
        Number(viewId),
        props.defaultAppearance,
      ),
    };
  });
});

onMounted(async () => {
  if (!props.productType) return;

  const data = await publishingCoreService.getCompositionPlacementPreviews(
    props.composition.id,
    props.productType.id,
    props.productType.hotspots || [],
    props.productType.defaultAppearance,
  );
  previews.value = data.previews;
});
</script>

<style lang="scss" scoped>
@import 'src/scss/styleguide/colors';
@import 'src/scss/constants';

.images {
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 16px;

  li {
    border-radius: $border-radius-small;
    border: 1px solid $grey15;
    background-color: $grey5;
    padding: 16px;
    position: relative;

    img {
      width: 100%;
    }

    .primary-badge {
      position: absolute;
      top: 8px;
      left: 8px;
    }
  }
}
</style>
